// frontend/src/components/NatalWheel.js
import React from "react";

const WHEEL_SIZE = 420;
const CENTER = WHEEL_SIZE / 2;

// Conversión grados → radianes (rotado para que 0° quede arriba)
function degToRad(deg) {
  return ((deg - 90) * Math.PI) / 180;
}

function posOnCircle(radius, degree) {
  const rad = degToRad(degree);
  return {
    x: CENTER + radius * Math.cos(rad),
    y: CENTER + radius * Math.sin(rad),
  };
}

// Símbolos planetarios
const planetSymbols = {
  Sol: "☉",
  Luna: "☽",
  Mercurio: "☿",
  Venus: "♀",
  Marte: "♂",
  Júpiter: "♃",
  Saturno: "♄",
  Urano: "♅",
  Neptuno: "♆",
  Plutón: "♇",
  Ascendente: "ASC",
  Descendente: "DES",
  "Medio Cielo": "MC",
  "Fondo del Cielo": "IC",
  "Nodo Norte": "☊",
  Lilith: "⚸",
  Quirón: "⚷",

  // aliases en inglés (por si el backend aún envía EN)
  Sun: "☉",
  Moon: "☽",
  Mercury: "☿",
  Mars: "♂",
  Jupiter: "♃",
  Saturn: "♄",
  Uranus: "♅",
  Neptune: "♆",
  Pluto: "♇",
  Ascendant: "ASC",
  Descendant: "DES",
  MC: "MC",
  IC: "IC",
  "True Node": "☊",
  "Mean Node": "☊",
  Chiron: "⚷",
};

// Radios base por tipo de “cuerpo”
function getPlanetRadius(name) {
  const n = String(name || "");

  // anillo externo: puntos angulares
  if (["Ascendant","Ascendente","Descendant","Descendente","MC","Medio Cielo","IC","Fondo del Cielo"].includes(n)) {
    return CENTER - 45;
  }

  // anillo medio: planetas personales
  if (["Sun","Sol","Moon","Luna","Mercury","Mercurio","Venus","Mars","Marte"].includes(n)) {
    return CENTER - 80;
  }

  // anillo interno: resto
  return CENTER - 110;
}

// Distribución profesional dentro de cada signo para evitar que se encimen
function computePlanetLayout(planets = []) {
  const normalized = planets.map((p) => ({
    ...p,
    name: p.name || p.planet || "", // por si viene con otra key
    degree: typeof p.degree === "number" ? p.degree : p.fullDegree || 0,
  }));

  // Agrupamos por sector de 30° (signo)
  const bySector = new Map();
  normalized.forEach((p) => {
    const baseDeg = ((p.degree % 360) + 360) % 360;
    const sector = Math.floor(baseDeg / 30); // 0..11
    if (!bySector.has(sector)) bySector.set(sector, []);
    bySector.get(sector).push({ ...p, baseDeg });
  });

  const MIN_ANGULAR_STEP = 6; // separación en grados dentro del mismo signo

  const laidOut = [];

  bySector.forEach((group) => {
    // Ordenamos por grado dentro del signo
    group.sort((a, b) => a.baseDeg - b.baseDeg);

    const n = group.length;
    if (n === 1) {
      const p = group[0];
      laidOut.push({
        ...p,
        displayDegree: p.baseDeg,
        displayRadius: getPlanetRadius(p.name),
      });
      return;
    }

    // Centramos el “ramillete” alrededor de los grados reales
    const midIndex = (n - 1) / 2;
    group.forEach((p, idx) => {
      const offsetIndex = idx - midIndex; // -1.5, -0.5, 0.5, 1.5, etc.
      const offsetDeg = offsetIndex * MIN_ANGULAR_STEP;

      const sectorStart = p.baseDeg - (p.baseDeg % 30);
      const clampedDeg = Math.min(
        Math.max(p.baseDeg + offsetDeg, sectorStart + 1),
        sectorStart + 29
      );

      laidOut.push({
        ...p,
        displayDegree: clampedDeg,
        displayRadius: getPlanetRadius(p.name),
      });
    });
  });

  return laidOut;
}

function translatePlanetName(name) {
  const map = {
    Sun: "Sol",
    Moon: "Luna",
    Mercury: "Mercurio",
    Venus: "Venus",
    Mars: "Marte",
    Jupiter: "Júpiter",
    Saturn: "Saturno",
    Uranus: "Urano",
    Neptune: "Neptuno",
    Pluto: "Plutón",
    Ascendant: "Ascendente",
    Descendant: "Descendente",
    MC: "Medio Cielo",
    IC: "Fondo del Cielo",
    "True Node": "Nodo Norte",
    "Mean Node": "Nodo Norte",
    Lilith: "Lilith",
    Chiron: "Quirón",
  };
  return map[name] || name;
}

export default function NatalWheel({ planets = [], houses = [] }) {
  // Evitar “dobles emojis” por duplicados del feed (muy común con Mean/True Node)
  const dedupedPlanets = React.useMemo(() => {
    const arr = Array.isArray(planets) ? planets : [];

    // Normalizamos nombre
    const norm = arr
      .map((p) => ({
        ...p,
        name: p.name || p.planet || p.planetName || (p.planet && p.planet.en) || "",
      }))
      .filter((p) => p.name);

    // Si vienen los dos nodos, mostramos SOLO True Node (el más usado)
    const hasTrue = norm.some((p) => p.name === "True Node");
    const filtered = hasTrue ? norm.filter((p) => p.name !== "Mean Node") : norm;

    // Dedupe por nombre (si algún provider repite un planeta)
    const seen = new Set();
    const out = [];
    for (const p of filtered) {
      const k = String(p.name);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(p);
    }
    return out;
  }, [planets]);

  const safeHouses = React.useMemo(() => (Array.isArray(houses) ? houses : []), [houses]);
  const layoutPlanets = React.useMemo(() => computePlanetLayout(dedupedPlanets), [dedupedPlanets]);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${WHEEL_SIZE} ${WHEEL_SIZE}`}
      style={{
        maxWidth: 420,
        display: "block",
        margin: "0 auto",
        filter: "drop-shadow(0 0 12px rgba(218,165,32,0.35))",
      }}
    >
      {/* Fondo */}
      <defs>
        <radialGradient id="bgGrad">
          <stop offset="0%" stopColor="#3b1a7a" />
          <stop offset="60%" stopColor="#1b083c" />
          <stop offset="100%" stopColor="#05010f" />
        </radialGradient>

        {/* Borde suave entre casas */}
        <linearGradient id="houseLineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(218,165,32,0.9)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.6)" />
        </linearGradient>
      </defs>

      <circle cx={CENTER} cy={CENTER} r={CENTER - 4} fill="url(#bgGrad)" stroke="#DAA520" strokeWidth={3} />

      {/* Círculo interior para separar casas de planetas */}
      <circle cx={CENTER} cy={CENTER} r={CENTER - 60} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={1.5} />

      {/* 12 sectores zodiacales (anillos suaves) */}
      {[...Array(12)].map((_, i) => {
        const start = i * 30;
        const end = start + 30;
        return (
          <path
            key={i}
            d={describeArc(CENTER, CENTER, CENTER - 5, start, end)}
            fill="none"
            stroke="rgba(255,255,255,0.10)"
            strokeWidth={2}
          />
        );
      })}

      {/* Signo en cada casa */}
      {(() => {
        const zodiacSymbols = {
          Aries: "♈",
          Taurus: "♉",
          Gemini: "♊",
          Cancer: "♋",
          Leo: "♌",
          Virgo: "♍",
          Libra: "♎",
          Scorpio: "♏",
          Sagittarius: "♐",
          Capricorn: "♑",
          Aquarius: "♒",
          Pisces: "♓",

          "Tauro": "♉",
          "Géminis": "♊",
          "Geminis": "♊",
          "Cáncer": "♋",
          "Escorpio": "♏",
          "Sagitario": "♐",
          "Capricornio": "♑",
          "Acuario": "♒",
          "Piscis": "♓",
        };

        const normalizeDeg = (d) => {
          const v = Number(d) || 0;
          return ((v % 360) + 360) % 360;
        };

        const getHouseNum = (h, idx) => Number(h.house ?? h.House ?? idx + 1);

        const sorted = safeHouses
          .map((h, idx) => ({ ...h, __idx: idx }))
          .sort((a, b) => getHouseNum(a, a.__idx) - getHouseNum(b, b.__idx));

        if (!sorted.length) return null;

        return sorted.map((h, i) => {
          const next = sorted[(i + 1) % sorted.length];
          const a = normalizeDeg(h.degree ?? h.normDegree ?? 0);
          let b = normalizeDeg(next.degree ?? next.normDegree ?? 0);
          if (b <= a) b += 360;
          const mid = (a + b) / 2;

          const signName = h.zodiac_sign?.name?.en || h.zodiacSign || h.sign || "";
          const sym = zodiacSymbols[signName] || "";
          if (!sym) return null;

          const { x, y } = posOnCircle(CENTER - 32, mid);

          return (
            <g key={`hsign-${i}`}>
              <circle cx={x} cy={y} r={12} fill="rgba(5,1,15,0.65)" stroke="rgba(218,165,32,0.55)" strokeWidth={1} />
              <text
                x={x}
                y={y + 7}
                fontSize="18"
                fill="#FFD700"
                fontWeight="900"
                textAnchor="middle"
                style={{
                  filter: "drop-shadow(0 0 3px rgba(75,0,130,0.9))",
                  pointerEvents: "none",
                }}
              >
                {sym}
              </text>
            </g>
          );
        });
      })()}

      {/* Líneas de casas */}
      {safeHouses.map((h, i) => {
        const degree = h.degree ?? h.normDegree ?? 0;
        const { x, y } = posOnCircle(CENTER - 5, degree);
        return (
          <line
            key={h.house || h.House || i}
            x1={CENTER}
            y1={CENTER}
            x2={x}
            y2={y}
            stroke="url(#houseLineGrad)"
            strokeWidth={1.6}
            strokeOpacity={0.9}
          />
        );
      })}

      {/* Números de casas */}
      {safeHouses.map((h, i) => {
        const degree = h.degree ?? h.normDegree ?? 0;
        const pos = posOnCircle(CENTER - 14, degree + 15);
        return (
          <text
            key={h.house || h.House || i}
            x={pos.x}
            y={pos.y}
            fill="#FFD700"
            fontSize="14"
            fontWeight="900"
            textAnchor="middle"
            style={{ textShadow: "0 0 4px rgba(0,0,0,0.8)" }}
          >
            {h.house || h.House || i + 1}
          </text>
        );
      })}

      {/* Círculo central */}
      <circle cx={CENTER} cy={CENTER} r={40} fill="rgba(5,1,15,0.95)" stroke="rgba(218,165,32,0.7)" strokeWidth={1.5} />
      <text x={CENTER} y={CENTER - 4} textAnchor="middle" fontSize="14" fill="#FFD700" fontWeight="900">
        ZODIACUS
      </text>
      <text x={CENTER} y={CENTER + 12} textAnchor="middle" fontSize="10" fill="#C0A0FF">
        Carta Natal
      </text>

      {/* Planetas */}
      {layoutPlanets.map((p, i) => {
        const rawName = p.name || p.planet || "";
        const planetName = translatePlanetName(rawName);
        const symbol = planetSymbols[planetName] || (planetName ? planetName.charAt(0) : "?");
        const radius = p.displayRadius ?? getPlanetRadius(planetName);
        const degree = p.displayDegree ?? p.degree ?? 0;
        const { x, y } = posOnCircle(radius, degree);

        return (
          <g key={p.name || i}>
            <circle
              cx={x}
              cy={y}
              r={symbol.length > 1 ? 11 : 9}
              fill="#14052b"
              stroke="#DAA520"
              strokeWidth={1.8}
              style={{ filter: "drop-shadow(0 0 5px rgba(218,165,32,0.9))" }}
            />
            <text
              x={x}
              y={y + 4}
              textAnchor="middle"
              fontSize={symbol.length > 1 ? 9 : 12}
              fill="#FFD700"
              fontWeight="bold"
              style={{ pointerEvents: "none" }}
            >
              {symbol}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* Helper para generar arcos */
function describeArc(x, y, radius, startAngle, endAngle) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? "0" : "1";

  return ["M", start.x, start.y, "A", radius, radius, 0, largeArc, 0, end.x, end.y].join(" ");
}

function polarToCartesian(centerX, centerY, radius, angle) {
  const rad = ((angle - 90) * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(rad),
    y: centerY + radius * Math.sin(rad),
  };
}